import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ModalPortal from './Common/ModalPortal';
import './ImageCropModal.css';

interface ImageCropModalProps {
  imageFile: File;
  onCropComplete: (croppedBlob: Blob, fileName: string) => void;
  onUseOriginal?: (file: File) => void;
  onCancel: () => void;
}

const ImageCropModal: React.FC<ImageCropModalProps> = ({ imageFile, onCropComplete, onUseOriginal, onCancel }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  React.useEffect(() => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result?.toString() || '');
    });
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        }
      }, imageFile.type);
    });
  }, [completedCrop, imageFile.type]);

  const handleCropConfirm = async () => {
    const croppedBlob = await getCroppedImg();
    if (croppedBlob) {
      onCropComplete(croppedBlob, imageFile.name);
    }
  };

  const handleUseOriginal = () => {
    if (onUseOriginal) {
      onUseOriginal(imageFile);
    }
  };

  return (
    <ModalPortal isOpen={true}>
      <div className="image-crop-modal-overlay">
        <div className="image-crop-modal">
        <div className="modal-header">
          <h3>
            <i className="fas fa-crop"></i>
            Ajustar Logomarca
          </h3>
          <button className="btn-close" onClick={onCancel}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {imageSrc && (
            <div className="crop-container">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                minWidth={50}
                minHeight={50}
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  onLoad={(e) => {
                    const { width, height } = e.currentTarget;
                    const size = Math.min(width, height) * 0.8;
                    setCrop({
                      unit: 'px',
                      width: size,
                      height: size,
                      x: (width - size) / 2,
                      y: (height - size) / 2
                    });
                  }}
                />
              </ReactCrop>
            </div>
          )}

          <div className="crop-instructions">
            <p>
              <i className="fas fa-info-circle"></i>
              Arraste e redimensione a área de seleção para ajustar sua logomarca.
              Você pode escolher qualquer proporção para o recorte ou usar a imagem original.
            </p>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>
            <i className="fas fa-times"></i>
            Cancelar
          </button>
          {onUseOriginal && (
            <button className="btn btn-info" onClick={handleUseOriginal}>
              <i className="fas fa-image"></i>
              Usar Imagem Original
            </button>
          )}
          <button className="btn btn-primary" onClick={handleCropConfirm}>
            <i className="fas fa-check"></i>
            Confirmar e Enviar
          </button>
        </div>
      </div>
      </div>
    </ModalPortal>
  );
};

export default ImageCropModal;
